import { createSignal, onMount, Show } from "solid-js";
import {
  goBack,
  HubbleRoute,
  PageAlignment,
  pushPage,
} from "~/shared_states/modal";
import { ButtonRegular } from "~/widgets/button";
import { NumberKeyboard } from "../voucher_edit/number_keyboard";
import { toRupees } from "~/utils/number";
import { clientRepo } from "~/server/apis/client_repo";
import { CoinPayoutSimulateResponse } from "~/server/types/payout";
import { SeeWhyBS } from "./see_why_bs";

interface BankTransferEditAmountModalProps {
  initialAmount: number;
  onAmountChange: (amount: number) => void;
}

export function BankTransferEditAmountModal(
  props: BankTransferEditAmountModalProps
) {
  const [amount, setAmount] = createSignal(props.initialAmount);
  const [simulateResponse, setSimulateResponse] =
    createSignal<CoinPayoutSimulateResponse | null>(null);

  const simulatePayout = async (amount: number) => {
    try {
      const response = await clientRepo.simulatePayout({ amount });
      setSimulateResponse(response);
    } catch (error) {
      console.error("Failed to simulate payout:", error);
    }
  };

  onMount(() => {
    simulatePayout(amount());
  });

  return (
    <div class="relative flex h-full  w-full flex-col items-center justify-start bg-baseTertiaryLight">
      <div class="mt-4 h-1 w-[45px] rounded-[22px] bg-baseTertiaryDark" />

      <div class="mt-8 w-full px-5">
        <p class="mb-4 text-center text-mediumBold text-basePrimaryDark">
          Enter transfer amount
        </p>

        <div class="mb-4 w-full rounded-2xl border-2 border-textPrimary px-3 py-4 text-center">
          <p class="text-h2 text-textDark">{toRupees(amount())}</p>
        </div>

        <NumberKeyboard
          onChange={(value) => {
            const newAmount = value ?? 0;
            setAmount(newAmount);
            simulatePayout(newAmount);
            props.onAmountChange(newAmount);
          }}
          value={amount()}
          class="mx-auto box-border max-w-[355px]"
        />

        <div class="mt-6 h-px w-full rounded-[22px] bg-baseTertiaryDark" />

        <Show
          when={amount() > (simulateResponse()?.maxCalculatedInrPayout ?? 0)}
          fallback={
            <p class="py-4 text-center text-medium text-textDark">
              Enter amount up to{" "}
              {toRupees(simulateResponse()?.maxCalculatedInrPayout ?? 0)}
            </p>
          }
        >
          <p class="py-4 text-center text-medium text-errorDark">
            Max you can transfer{" "}
            {toRupees(simulateResponse()?.maxCalculatedInrPayout ?? 0)}{" "}
            <span
              class="text-mediumBold text-textDark underline"
              onClick={() => {
                pushPage(
                  () => (
                    <SeeWhyBS
                      amount={amount() ?? 0}
                      balance={simulateResponse()?.maxCalculatedInrPayout ?? 0}
                      redeemAmount={
                        simulateResponse()?.totalCoinsForRequestedAmount ?? 0
                      }
                      taxAmount={
                        simulateResponse()?.tdsCoinsForRequestedAmount ?? 0
                      }
                    />
                  ),
                  {
                    isModal: true,
                    alignment: PageAlignment.bottom,
                    offset: { top: 20, right: 20 },
                  },
                  HubbleRoute.BANK_SEE_WHY_BS
                );
              }}
            >
              See why
            </span>
          </p>
        </Show>

        <ButtonRegular
          class="mb-4 w-full disabled:bg-baseTertiaryDark"
          isEnabled={
            amount() > 0 &&
            amount() <= (simulateResponse()?.maxCalculatedInrPayout ?? 0)
          }
          onClick={() => {
            goBack();
          }}
        >
          Continue
        </ButtonRegular>
      </div>
    </div>
  );
}
